import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Download, AlertCircle, Package, Plus } from 'lucide-react';
import { adminOrders } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_OPTIONS = [
  'pending',
  'confirmed',
  'shipped',
  'delivered',
  'invoiced',
  'paid',
  'cancelled',
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'both', label: 'Both' },
];

const STATUS_BADGE = {
  pending:   'badge bg-warning text-dark',
  confirmed: 'badge bg-info text-dark',
  shipped:   'badge bg-primary',
  delivered: 'badge bg-success',
  invoiced:  'badge bg-secondary',
  paid:      'badge bg-dark',
  cancelled: 'badge bg-danger',
};

const formatCurrency = (value) =>
  value != null
    ? Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : '—';

const formatDate = (d) => {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
};

const EMPTY_FILTERS = {
  dateFrom:    '',
  dateTo:      '',
  status:      '',
  search:      '',
  paymentType: '',
};

export default function OrderList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesRep = user?.role === 'sales_rep';

  const [filters, setFilters]         = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setApplied]  = useState({ ...EMPTY_FILTERS });
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [exporting, setExporting]     = useState(false);

  const fetchOrders = useCallback(async (activeFilters) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminOrders.list({
        dateFrom:    activeFilters.dateFrom    || undefined,
        dateTo:      activeFilters.dateTo      || undefined,
        status:      activeFilters.status      || undefined,
        search:      activeFilters.search      || undefined,
        paymentType: activeFilters.paymentType || undefined,
      });
      const data = res?.data?.data;
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load orders.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount with empty filters
  useEffect(() => {
    fetchOrders(EMPTY_FILTERS);
  }, [fetchOrders]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setApplied({ ...filters });
    fetchOrders(filters);
  };

  const handleClear = () => {
    const cleared = { ...EMPTY_FILTERS };
    setFilters(cleared);
    setApplied(cleared);
    fetchOrders(cleared);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await adminOrders.export(appliedFilters);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href    = url;
      a.download = 'orders.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleRowClick = (orderID) => {
    navigate(`/admin/orders/${orderID}`);
  };

  return (
    <div className="container-fluid py-4">
      {isSalesRep && (
        <div className="alert alert-info d-flex align-items-center gap-2 py-2 mb-3">
          <span className="badge bg-info text-dark me-1">Your Accounts</span>
          Showing orders for your assigned accounts only.
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0 fw-bold">Orders</h2>
        <div className="d-flex gap-2">
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() => navigate('/admin/orders/create')}
          >
            <Plus size={16} />
            Create Order
          </button>
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            onClick={handleExport}
            disabled={exporting}
          >
            <Download size={16} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <form onSubmit={handleSearch} className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-sm-6 col-md-2">
              <label htmlFor="dateFrom" className="form-label fw-semibold small mb-1">
                Date From
              </label>
              <input
                type="date"
                id="dateFrom"
                name="dateFrom"
                className="form-control form-control-sm"
                value={filters.dateFrom}
                onChange={handleChange}
              />
            </div>

            <div className="col-sm-6 col-md-2">
              <label htmlFor="dateTo" className="form-label fw-semibold small mb-1">
                Date To
              </label>
              <input
                type="date"
                id="dateTo"
                name="dateTo"
                className="form-control form-control-sm"
                value={filters.dateTo}
                onChange={handleChange}
              />
            </div>

            <div className="col-sm-6 col-md-2">
              <label htmlFor="status" className="form-label fw-semibold small mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                className="form-select form-select-sm"
                value={filters.status}
                onChange={handleChange}
              >
                <option value="">All</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-sm-6 col-md-3">
              <label htmlFor="search" className="form-label fw-semibold small mb-1">
                Customer Search
              </label>
              <input
                type="text"
                id="search"
                name="search"
                className="form-control form-control-sm"
                placeholder="Company name or email…"
                value={filters.search}
                onChange={handleChange}
              />
            </div>

            <div className="col-sm-6 col-md-2">
              <label htmlFor="paymentType" className="form-label fw-semibold small mb-1">
                Payment Type
              </label>
              <select
                id="paymentType"
                name="paymentType"
                className="form-select form-select-sm"
                value={filters.paymentType}
                onChange={handleChange}
              >
                <option value="">All</option>
                {PAYMENT_TYPE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-sm-6 col-md-1 d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary btn-sm d-flex align-items-center gap-1 flex-grow-1"
                disabled={loading}
              >
                <Search size={14} />
                Search
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center"
                onClick={handleClear}
                title="Clear filters"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            aria-label="Close"
            onClick={() => setError(null)}
          />
        </div>
      )}

      {/* Loading Spinner */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <span className="ms-3 text-muted">Loading orders…</span>
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          {orders.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <Package size={48} className="mb-3 opacity-50" />
              <p className="mb-0 fs-5">No orders found.</p>
              <p className="small">Try adjusting your filters or clear them to see all orders.</p>
            </div>
          ) : (
            <div className="card shadow-sm">
              <div className="table-responsive">
                <table className="table table-hover table-striped align-middle mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th scope="col">Order #</th>
                      <th scope="col">Customer</th>
                      <th scope="col">Date</th>
                      <th scope="col">Items</th>
                      <th scope="col">Total</th>
                      <th scope="col">Fulfillment</th>
                      <th scope="col">Status</th>
                      <th scope="col">Payment Status</th>
                      <th scope="col">Due Date</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.orderID}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRowClick(order.orderID)}
                      >
                        <td className="fw-semibold">#{order.orderID}</td>
                        <td>
                          <div className="fw-semibold">{order.companyName || '—'}</div>
                          {order.email && (
                            <div className="text-muted small">{order.email}</div>
                          )}
                        </td>
                        <td className="text-nowrap">{formatDate(order.orderDate)}</td>
                        <td>
                          {order.itemCount != null ? (
                            <span>
                              {order.itemCount} item{order.itemCount !== 1 ? 's' : ''}
                              {order.totalCases != null && (
                                <span className="text-muted small d-block">
                                  {order.totalCases} case{order.totalCases !== 1 ? 's' : ''}
                                </span>
                              )}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="text-nowrap fw-semibold">
                          {formatCurrency(order.totalAmount)}
                        </td>
                        <td>
                          {order.fulfillmentType === 'pickup' && (
                            <div className="d-flex flex-column gap-1">
                              <span className="badge bg-success">Pickup</span>
                              {order.pickup_status === 'ready' && (
                                <span className="badge bg-info">Ready</span>
                              )}
                              {order.pickup_status === 'picked_up' && (
                                <span className="badge bg-dark">Picked Up</span>
                              )}
                              {(!order.pickup_status || order.pickup_status === 'pending') && (
                                <span className="badge bg-secondary">Pending</span>
                              )}
                            </div>
                          )}
                          {order.fulfillmentType === 'delivery' && (
                            <span className="badge bg-warning text-dark">Delivery</span>
                          )}
                          {(!order.fulfillmentType || order.fulfillmentType === 'ship') && (
                            <span className="badge bg-primary">Ship</span>
                          )}
                        </td>
                        <td>
                          {order.orderStatus ? (
                            <span className={STATUS_BADGE[order.orderStatus] || 'badge bg-secondary'}>
                              {order.orderStatus.charAt(0).toUpperCase() + order.orderStatus.slice(1)}
                            </span>
                          ) : '—'}
                        </td>
                        <td>
                          {order.paymentStatus ? (
                            <span className={STATUS_BADGE[order.paymentStatus] || 'badge bg-secondary'}>
                              {order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="text-nowrap">{formatDate(order.invoiceDueDate)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleRowClick(order.orderID)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-footer text-muted small">
                {orders.length} order{orders.length !== 1 ? 's' : ''} found
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
